import React from 'react';
import classNames from 'classnames/bind';
import {
  DatesRangeValue,
  DateTimePicker,
  DateTimePickerProps,
  DateValue,
} from '@mantine/dates';
import { SingleValue } from 'react-select';
import { useInterval, useUncontrolled } from '@mantine/hooks';
import { DateTime } from 'luxon';

import { formatPretty } from 'services/string';
import { WIDGET_REFETCH_INTERVAL } from 'dashboards/services/interval';

import {
  DATE_RANGE_PRESETS,
  RelativeDateRange,
} from 'components/DateRangePickerInput/time_range';
import { Icon } from 'components/Icon/IconDeprecated';
import { Select } from 'components/Select/Select';

import calendarStyles from '../DatePickerInput/DatePickerInput.module.scss';
import styles from './DateTimeRangeInput.module.scss';

const c = classNames.bind(styles);
const calendarClassNames = classNames.bind(calendarStyles);

const DATE_RANGE_PRESET_OPTIONS = Object.keys(DATE_RANGE_PRESETS).map(
  (value) => ({
    value,
    label: formatPretty(value),
  })
);

export type DateRange = DatesRangeValue | RelativeDateRange;

export type DateTimeRangeInputProps = Omit<
  DateTimePickerProps,
  'icon' | 'type' | 'classNames' | 'defaultValue' | 'value' | 'onChange'
> & {
  defaultValue?: DateRange;
  value?: DateRange;
  /** Update date range every few seconds to allow displaying live data */
  liveUpdate?: boolean;
  onChange?: (dateRange?: DatesRangeValue, preset?: string) => void;
};

export function DateTimeRangeInput({
  className,
  onChange,
  value,
  defaultValue = 'TODAY',
  liveUpdate,
  disabled,
  ...props
}: DateTimeRangeInputProps) {
  const id = React.useId();

  const [dateRange, setDateRange] = useUncontrolled<DateRange>({
    value,
    defaultValue,
    onChange(value) {
      if (Array.isArray(value)) {
        onChange?.(value, 'CUSTOM');
      } else {
        onChange?.(DATE_RANGE_PRESETS[value](), value);
      }
    },
  });

  const isDateRangeArray = Array.isArray(dateRange);
  const dates = isDateRangeArray ? dateRange : DATE_RANGE_PRESETS[dateRange]();
  const preset = isDateRangeArray ? 'CUSTOM' : dateRange;

  const [start, end] = dates;

  const isSameDay =
    start &&
    end &&
    DateTime.fromJSDate(start).hasSame(DateTime.fromJSDate(end), 'day');

  // Update date range over time if preset is selected and different from current value
  const interval = useInterval(() => {
    if (!preset || preset === 'CUSTOM' || !liveUpdate) {
      return;
    }

    const newDates = DATE_RANGE_PRESETS[preset]();

    if (!newDates) {
      return;
    }

    const [newStart, newEnd] = newDates;

    if (
      !dates ||
      newStart.toISOString() !== dates[0]?.toISOString() ||
      newEnd.toISOString() !== dates[1]?.toISOString()
    ) {
      setDateRange(preset as RelativeDateRange);
    }
  }, WIDGET_REFETCH_INTERVAL);

  React.useEffect(() => {
    // Interval is inactive (-1) if no preset is selected
    if (!preset || preset === 'CUSTOM') {
      return;
    }

    interval.start();
    return interval.stop;
  }, [preset, interval]);

  function handlePresetChange(
    option: SingleValue<{ value: string; label: string }>
  ) {
    if (!option || !option.value) {
      return;
    }

    setDateRange(option.value as RelativeDateRange);
  }

  function handleStartChange(value: DateValue) {
    // Ensure start date doesn't exceed end date
    const constrainedStart = end && value && value > end ? end : value;
    setDateRange([constrainedStart, end]);
  }

  function handleEndChange(value: DateValue) {
    // Ensure end date isn't before start date
    const constrainedEnd = start && value && value < start ? start : value;
    setDateRange([start, constrainedEnd]);
  }

  return (
    <div className={c('wrap', className)}>
      <Select
        className={c('presets')}
        options={DATE_RANGE_PRESET_OPTIONS}
        onChange={handlePresetChange}
        value={{ value: preset, label: formatPretty(preset) }}
        size="small"
        isDisabled={disabled}
      />

      <div className={c('range')}>
        <label htmlFor={id}>
          <Icon className={c('icon')} name="calendar" unstyled />
        </label>
        <DateTimePicker
          id={id}
          {...props}
          classNames={{
            input: c('input', className),
            day: calendarClassNames('calendar-day', 'interactive'),
            calendarHeaderLevel: calendarClassNames('interactive'),
            calendarHeaderControl: calendarClassNames('interactive'),
          }}
          valueFormat="MMM DD, YYYY HH:mm"
          placeholder="Start"
          value={start}
          maxDate={isSameDay && end ? end : undefined}
          excludeDate={(date) => Boolean(end) && Number(date) > Number(end)}
          onChange={handleStartChange}
        />
        <span>&ndash;</span>
        <DateTimePicker
          {...props}
          classNames={{
            input: c('input', className),
            day: calendarClassNames('calendar-day', 'interactive'),
            calendarHeaderLevel: calendarClassNames('interactive'),
            calendarHeaderControl: calendarClassNames('interactive'),
          }}
          placeholder="End"
          valueFormat="MMM DD, YYYY HH:mm"
          value={end}
          minDate={isSameDay && start ? start : undefined}
          excludeDate={(date) => Boolean(start) && Number(date) < Number(start)}
          onChange={handleEndChange}
        />
      </div>
    </div>
  );
}
